/**
 * SectionHeader — Title (mit optionalem Sub-Text) links, Right-Slot
 * (z.B. „Alle →"-Link oder Action-Button) rechts.
 *
 * Eingeführt in Slice-2 Redesign (2026-05-09). Pattern aus
 * `design-handoff-8-5-2026/v1/layout.jsx` Lines 253–263.
 *
 * Pure präsentational, kein State.
 *
 * `mb` ist als finite union typed (UI-D4.5, 2026-05-23) damit die Klassen
 * als statische Literale in Tailwinds Source-Scan landen — dynamische
 * Klassen-String-Interpolation ist Tailwind-v4-unsicher (nicht im
 * Build-Output enthalten). Falls ein neuer Wert nötig: Map ergänzen.
 */
import type { ReactNode } from 'react';

type MbKey = 0 | 1 | 2 | 3 | 4 | 6 | 8;

/** Titel-Größe. 'sm' (14px) = Default/bestehendes Verhalten. 'base' (16px) =
 *  prominentere Section-Header, damit sie als Header und nicht als Label lesen
 *  (Dashboard, P0 Hierarchie 2026-06-10). Finite union → statische Literale im
 *  Tailwind-Source-Scan (analog mb). */
type TitleSize = 'sm' | 'base';

type Props = {
  title: string;
  sub?: string;
  right?: ReactNode;
  /** mb-distanz in tailwind-units, default 4 (= 14px). */
  mb?: MbKey;
  titleSize?: TitleSize;
  className?: string;
};

const MB_CLASS: Record<MbKey, string> = {
  0: 'mb-0',
  1: 'mb-1',
  2: 'mb-2',
  3: 'mb-3',
  4: 'mb-4',
  6: 'mb-6',
  8: 'mb-8',
};

const TITLE_CLASS: Record<TitleSize, string> = {
  sm: 'text-sm',
  base: 'text-base',
};

export function SectionHeader({
  title,
  sub,
  right,
  mb = 4,
  titleSize = 'sm',
  className = '',
}: Props) {
  return (
    <div className={`flex items-end justify-between ${MB_CLASS[mb]} ${className}`}>
      <div>
        <h2 className={`${TITLE_CLASS[titleSize]} font-medium tracking-tight m-0 leading-tight`}>
          {title}
        </h2>
        {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
      </div>
      {right}
    </div>
  );
}
